import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { DashboardData } from '@/dashboard/dashboard.types';
import { environment } from 'src/environments/environment';
import { Role, RoleName, RoleService } from '@/services/role.service';

interface MenuEntry {
    label: string;
    icon: string;
    role?: RoleName;
    badgeUrl?: string;
    routerLink?: string[];
    items?: MenuEntry[];
}

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        <ng-container *ngFor="let item of model; let i = index">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul> `
})
export class AppMenu {
    private httpClient = inject(HttpClient);
    private roleService = inject(RoleService);

    model: any[] = [];

    ngOnInit() {
        this.model = this.visibleItems([
            {
                label: '',
                icon: '',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'pi pi-fw pi-home',
                        role: Role.BaseAccessDashboard,
                        routerLink: ['/']
                    },
                    {
                        label: 'Mitglieder',
                        icon: 'pi pi-fw pi-users',
                        items: [
                            {
                                label: 'Übersicht',
                                icon: 'pi pi-fw pi-users',
                                role: Role.BaseAccessMember,
                                routerLink: ['/members/overview']
                            },
                            {
                                label: 'Neu',
                                icon: 'pi pi-fw pi-user-plus',
                                role: Role.CreateMember,
                                routerLink: ['/members/new']
                            },
                            {
                                label: 'Wartefrist',
                                badgeUrl: '/api/dashboard/waiting-period-end',
                                icon: 'pi pi-fw pi-clock',
                                role: Role.BaseAccessMember,
                                routerLink: ['/members/waiting-period']
                            },
                            {
                                label: 'Mitgliederkarten',
                                badgeUrl: '/api/dashboard/member-card',
                                icon: 'pi pi-fw pi-credit-card',
                                role: Role.ProcessMemberCard,
                                routerLink: ['/members/cards']
                            },
                            {
                                label: 'Adresse unbekannt',
                                badgeUrl: '/api/dashboard/address-unknown',
                                icon: 'pi pi-fw pi-phone',
                                role: Role.BaseAccessMember,
                                routerLink: ['/members/address-unknown']
                            },
                            {
                                label: 'Scheidung',
                                badgeUrl: '/api/dashboard/member-divorce',
                                icon: 'pi pi-fw pi-hammer',
                                role: Role.BaseAccessMember,
                                routerLink: ['/members/divorce']
                            },
                            {
                                label: 'Volljährig',
                                badgeUrl: '/api/dashboard/member-grownup',
                                icon: 'pi pi-fw pi-user',
                                role: Role.BaseAccessMember,
                                routerLink: ['/members/grownup']
                            }
                        ]
                    },
                    {
                        label: 'Finanzen',
                        icon: 'pi pi-fw pi-wallet',
                        items: [
                            {
                                label: 'Cockpit',
                                icon: 'pi pi-fw pi-chart-line',
                                role: Role.BaseAccessFinance,
                                routerLink: ['/finance/cockpit']
                            },
                            {
                                label: 'Bankimport',
                                badgeUrl: '/api/dashboard/bankimport-error',
                                icon: 'pi pi-fw pi-building-columns',
                                role: Role.BaseAccessFinance,
                                routerLink: ['/finance/bank-import']
                            },
                            {
                                label: 'Beitragsjahre',
                                icon: 'pi pi-fw pi-calendar',
                                role: Role.BaseAccessFinance,
                                routerLink: ['/finance/contribution-years']
                            }
                        ]
                    },
                    {
                        label: 'Verwaltung',
                        icon: 'pi pi-fw pi-cog',
                        items: [
                            {
                                label: 'Aufträge',
                                icon: 'pi pi-fw pi-briefcase',
                                role: Role.BaseAccessJob,
                                routerLink: ['/jobs/overview']
                            },
                            {
                                label: 'Änderungsanträge',
                                icon: 'pi pi-fw pi-sync',
                                badgeUrl: '/api/dashboard/modification-submitted',
                                role: Role.ProcessModification,
                                routerLink: ['/modifications/overview']
                            }
                        ]
                    }
                ]
            }
        ]);
    }

    /** Entfernt Eintraege ohne Rolle sowie leere Gruppen und laedt Badges nur fuer sichtbare Eintraege. */
    private visibleItems(items: MenuEntry[]): any[] {
        return items.flatMap(({ role, badgeUrl, ...item }) => {
            if (role && !this.roleService.hasRole(role)) return [];
            if (!item.items) return [{ ...item, ...(badgeUrl && { badge: this.badge(badgeUrl) }) }];
            const children = this.visibleItems(item.items);
            return children.length ? [{ ...item, items: children }] : [];
        });
    }

    /** Offene Aufgaben als Zaehler im Menue; 0 oder Fehler blendet den Badge aus. */
    private badge(url: string) {
        const count = signal<number | null>(null);
        this.httpClient.get<DashboardData>(`${environment.apiBase}${url}`, { withCredentials: true }).subscribe({
            next: (res) => count.set(res.totalCount || null),
            error: () => count.set(null)
        });
        return count;
    }
}
